import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { CalendarClock, Clock, RefreshCw } from 'lucide-react';

type Props = {
  /** Date d'ouverture des inscriptions (ISO). */
  opensAt: string;
  competitionName: string;
  competitionId: number;
};

type Remaining = { days: number; hours: number; minutes: number; seconds: number; done: boolean };

function computeRemaining(target: number): Remaining {
  const diff = target - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    done: false,
  };
}

/**
 * Feature 7 (2026-08) — écran affiché à la place du formulaire d'inscription
 * quand la date d'ouverture des inscriptions n'est pas encore atteinte.
 * Compte à rebours dynamique (tick chaque seconde) ; à 0, propose d'accéder au
 * formulaire (rechargement pour repasser les gardes serveur/front).
 */
export default function RegistrationCountdown({ opensAt, competitionName, competitionId }: Props) {
  const target = new Date(opensAt).getTime();
  const [remaining, setRemaining] = useState<Remaining>(() => computeRemaining(target));

  useEffect(() => {
    const tick = () => setRemaining(computeRemaining(target));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const openDate = new Date(opensAt);
  const dateLabel = openDate.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const timeLabel = openDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const units: { value: number; label: string }[] = [
    { value: remaining.days, label: remaining.days > 1 ? 'jours' : 'jour' },
    { value: remaining.hours, label: 'heures' },
    { value: remaining.minutes, label: 'minutes' },
    { value: remaining.seconds, label: 'secondes' },
  ];

  return (
    <div className='bg-[#F8FAFC] min-h-screen pt-32 pb-20 flex items-center justify-center px-6'>
      <div className='w-full max-w-2xl'>
        <div className='bg-white border border-slate-200 rounded-3xl shadow-sm p-8 sm:p-10 text-center'>
          <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5'>
            <Clock className='w-8 h-8 text-blue-600' />
          </div>

          <p className='text-sm font-semibold uppercase tracking-wide text-blue-600 mb-2'>
            Inscriptions pas encore ouvertes
          </p>
          <h1 className='text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2'>{competitionName}</h1>
          <p className='text-slate-500 mb-8'>
            Les inscriptions à ce concours ouvriront prochainement. Reviens à la date indiquée pour inscrire ton équipe.
          </p>

          {remaining.done ? (
            <div className='space-y-6'>
              <div className='bg-emerald-50 border border-emerald-200 rounded-2xl p-5'>
                <p className='text-lg font-extrabold text-emerald-700'>C&apos;est ouvert&nbsp;!</p>
                <p className='text-sm text-emerald-600 mt-1'>Les inscriptions viennent d&apos;ouvrir.</p>
              </div>
              <button
                type='button'
                onClick={() => window.location.reload()}
                className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
              >
                <RefreshCw className='w-4 h-4' />
                Accéder à l&apos;inscription
              </button>
            </div>
          ) : (
            <>
              <div className='grid grid-cols-4 gap-3 sm:gap-4 mb-8'>
                {units.map(u => (
                  <div key={u.label} className='bg-slate-50 border border-slate-100 rounded-2xl py-4'>
                    <div className='text-3xl sm:text-4xl font-extrabold text-slate-900 tabular-nums'>
                      {String(u.value).padStart(2, '0')}
                    </div>
                    <div className='text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-400 mt-1'>
                      {u.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2 text-sm font-semibold text-blue-700'>
                <CalendarClock className='w-4 h-4' />
                Ouverture le {dateLabel} à {timeLabel}
              </div>
            </>
          )}

          <div className='mt-8'>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competitionId))}
              className='inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors'
            >
              Voir le concours
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
